<template>
  <div>
    <div class="text-center d-flex">
      <GradientButton
        v-if="testator.hasActivePayment && !whitelabel.will_async"
        @gradientButtonClicked="onPreviewClick()"
        title="Preview Will"
        :small="true"
        :fill="buttonFill"
        :callout="'right'"
        :disabled="disabled"
        :loading="buttonLoading"
      />
      <GradientButton
        v-if="testator.hasActivePayment && whitelabel.will_async"
        @gradientButtonClicked="onPreviewClick()"
        :title="downloadText"
        :small="true"
        :fill="buttonFill"
        :callout="'right'"
        :disabled="disabled"
        :loading="buttonLoading"
      />
    </div>

    <WillLanguageDialog
      :testator="testator"
      :willLanguageDialog="showWillLanguageDialog"
      :buttonLoading="buttonLoading"
      :allowedLanguageCodes="companyWillDownloadLanguages"
      @willLanguageDialog="handleLanguageDialogClose"
      @languageSelected="handleLanguageSelection"
    />

    <ErrorSnackbar v-model="showError" :error="errorMessage" />
  </div>
</template>

<script>
  import {
    PREVIEW_WILL_MUTATION,
    GET_WILL_PREVIEW_MUTATION,
  } from '../../constants/graphql/wills';
  import { UPDATE_USER_LOCALE_MUTATION } from '../../constants/graphql/users';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import WillLanguageDialog from './dialogs/WillLanguageDialog.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';
  import { TESTATOR_STEPS_QUERY } from '../../constants/graphql/overview';

  export default {
    name: 'PreviewWill',
    components: {
      ErrorSnackbar,
      WillLanguageDialog,
    },
    mixins: [formHelperMixin],
    props: {
      testator: {
        required: true,
        type: Object,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      callToAction: {
        type: Boolean,
        default: true,
      },
      previewWillDetailsChanged: {
        required: true,
        default: false,
        type: Boolean,
      },
    },
    data() {
      return {
        errorMessage: null,
        intervalId: null,
        newTab: null,
        downloadText: 'Processing Preview',
        showWillLanguageDialog: false,
        selectedLanguage: 'ENGLISH',
      };
    },
    computed: {
      buttonFill() {
        return this.previewWillDetailsChanged;
      },
      showLanguageDialogCondition() {
        const langs = this.testator?.company?.willDownloadLanguages;
        return Array.isArray(langs) && langs.length > 1;
      },
      companyWillDownloadLanguages() {
        return this.testator?.company?.willDownloadLanguages || ['en'];
      },
    },
    created() {
      if (!this.whitelabel.will_async) {
        this.buttonLoading = true;
        this.getWillPreview();
      } else {
        this.intervalId = setInterval(this.getWillPreview, 3000);
        this.buttonLoading = true;
        this.getWillPreview();
      }
    },
    methods: {
      onPreviewClick() {
        if (this.showLanguageDialogCondition) {
          this.showWillLanguageDialog = true;
        } else {
          this.downloadWill();
        }
      },
      showLanguageDialog() {
        this.showWillLanguageDialog = true;
      },
      handleLanguageDialogClose(value) {
        this.showWillLanguageDialog = value;
      },
      handleLanguageSelection(language) {
        this.selectedLanguage = language;
        this.showWillLanguageDialog = false;
        this.updateUserLocale(language);
      },
      updateUserLocale(language) {
        const localeMap = {
          ENGLISH: 'en',
          AFRIKAANS: 'af',
          ZULU: 'zu',
          XHOSA: 'xh',
          SOTHO: 'nso',
        };

        const locale = localeMap[language];
        const id = this.userId;

        this.$apollo
          .mutate({
            mutation: UPDATE_USER_LOCALE_MUTATION,
            variables: {
              id,
              locale,
            },
            update: (cache, { data: { updateUserLocale } }) => {
              this.updateCacheAfterUpdateUser(cache, updateUserLocale);
            },
          })
          .then(_response => {
            this.downloadWill();
          })
          .catch(error => {
            console.warn('Failed to update user locale:', error);
          });
      },
      updateCacheAfterUpdateUser(cache, updateUser) {
        try {
          const query = {
            query: TESTATOR_QUERY,
            variables: { id: this.testatorId },
          };
          const data = cache.readQuery(query);

          if (data && data.testator && data.testator.user) {
            data.testator.user.locale = updateUser.locale;
            cache.writeQuery({ ...query, data });
          }
        } catch (error) {
          console.warn('Failed to update cache:', error);
        }
      },
      downloadWill() {
        this.generateWillPreview();
      },
      generateWillPreview() {
        this.buttonLoading = true;
        if (!this.whitelabel.will_async) {
          this.newTab = window.open('/generating-will', '_blank');
        }

        this.$apollo
          .mutate({
            mutation: PREVIEW_WILL_MUTATION,
            variables: {
              testatorId: this.testator.id,
            },
            context: {
              fetchOptions: {
                signal: AbortSignal.timeout(60000),
              },
            },
          })
          .then(response => {
            if (response.data.previewWill.url) {
              if (!this.whitelabel.will_async) {
                // Do nothing
              } else {
                this.newTab = window.open('/generating-will', '_blank');
              }

              this.newTab.location.href = response.data.previewWill.url;
              this.newTab = null;
              this.buttonLoading = false;
              this.$emit('previewWillDownloaded');
            } else {
              if (!this.whitelabel.will_async) {
                // Do nothing
              } else {
                this.intervalId = setInterval(this.getWillPreview, 3000);
              }
            }
          })
          .catch(error => {
            this.buttonLoading = false;
            if (error.message.includes('GraphQL error: Executor invalid')) {
              this.errorMessage =
                'Your Executor choice appears to be invalid, please edit your Executor in your people overview to resolve the issue.';
              this.newTab.location.href = 'generating-will?error=2';
            } else {
              this.newTab.location.href = 'generating-will?error=1';
              throw error;
            }
            this.displayError();
          });
      },
      getWillPreview() {
        this.$apollo
          .mutate({
            mutation: GET_WILL_PREVIEW_MUTATION,
            variables: {
              testatorId: this.testatorId,
            },
            update: (cache, { data: { getWillPreview } }) => {
              if (
                getWillPreview.url &&
                getWillPreview.previewWillVersion &&
                getWillPreview.previewWillVersion.processed === true
              ) {
                this.updateCacheForTestatorQuery(
                  cache,
                  getWillPreview.previewWillVersion
                );
                this.updateCacheForTestatorStepsQuery(
                  cache,
                  getWillPreview.previewWillVersion
                );
              }
            },
          })
          .then(response => {
            if (response.data.getWillPreview.processWill) {
              this.downloadText = 'Process Preview';
              clearInterval(this.intervalId);
              this.buttonLoading = false;
            } else if (
              response.data.getWillPreview.url &&
              response.data.getWillPreview.previewWillVersion &&
              response.data.getWillPreview.previewWillVersion.processed === true
            ) {
              this.downloadText = 'Download Preview';
              clearInterval(this.intervalId);
              this.buttonLoading = false;
            }
          })
          .catch(error => {
            this.buttonLoading = false;
            throw error;
          });
      },
      updateCacheForTestatorQuery(cache, previewWillVersion) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        let data;
        try {
          data = cache.readQuery(query);
        } catch (e) {
          // Query isn't in cache (or is missing fields); nothing to update.
          return;
        }
        if (!data || !data.testator) return;
        // Only add previewWillVersion to testator if its a new previewWillVersion
        if (
          !data.testator.previewWillVersions.find(
            existingWillVersion =>
              existingWillVersion.id == previewWillVersion.id
          )
        ) {
          data.testator.previewWillVersions.push(previewWillVersion);
          cache.writeQuery({ ...query, data: data });
        }
      },
      updateCacheForTestatorStepsQuery(cache, previewWillVersion) {
        const query = {
          query: TESTATOR_STEPS_QUERY,
          variables: { id: this.testatorId },
        };
        let data;
        try {
          data = cache.readQuery(query);
        } catch (e) {
          // Query isn't in cache (or is missing fields); nothing to update.
          return;
        }
        if (!data || !data.testator) return;

        // Ensure required fields exist so read/writeQuery doesn't crash later.
        if (!Array.isArray(data.testator.previewWillVersions)) {
          data.testator.previewWillVersions = [];
        }
        if (typeof data.testator.willDetailsChanged === 'undefined') {
          data.testator.willDetailsChanged = false;
        }
        if (typeof data.testator.previewWillDetailsChanged === 'undefined') {
          data.testator.previewWillDetailsChanged = false;
        }

        // Only add previewWillVersion to testator if its a new previewWillVersion
        if (
          !data.testator.previewWillVersions.find(
            existingWillVersion =>
              existingWillVersion.id == previewWillVersion.id
          )
        ) {
          data.testator.previewWillVersions.push(previewWillVersion);
        }
        // Latest version created therefore all details up to date
        data.testator.previewWillDetailsChanged = false;
        cache.writeQuery({ ...query, data: data });
      },
    },
  };
</script>
<style>
  .custom-loader {
    animation: loader 1s infinite;
    display: flex;
  }
  @-moz-keyframes loader {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
  @-webkit-keyframes loader {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
  @-o-keyframes loader {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
  @keyframes loader {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
</style>
